/** Wire protocol v2: timestamps are UTC milliseconds; world units are metres. */
export const PROTOCOL_VERSION = 2;
export type Vec2 = { x: number; z: number };
export type AvatarAppearance = {
  presentation: 'boy' | 'girl'; skin: string; hair: string; outfit: string;
  hairstyle: 'short' | 'bob' | 'curls' | 'long' | 'pigtails' | 'ponytail' | 'bun' | 'mohawk';
  outfitStyle?: 'dress' | 'overalls' | 'shorts' | 'trousers' | 'skirt';
  bottom?: string; shoes?: string;
  accessory: 'party-hat' | 'bow' | 'star' | 'none' | 'glasses' | 'cap' | 'crown';
};
export type RsvpStatus = 'unanswered' | 'yes' | 'maybe' | 'no';
export type Behavior = 'wander' | 'socialize' | 'idle' | 'manual' | 'gather' | 'celebrate' | 'watch' | 'speaking' | 'offline';
export type MotionPlan = { points: Vec2[]; startedAt: number; speed: number; endsAt: number; revision: number };
export type AvatarState = {
  id: string; name: string; appearance: AvatarAppearance; position: Vec2;
  motion: MotionPlan | null; behavior: Behavior; facing: number; joinedAt: number;
  facePieUntil: number; reactionUntil: number;
  online?: boolean; speakingUntil?: number; lastSeenAt?: number;
  rsvp?: RsvpStatus; rsvpUpdatedAt?: number;
};
export type Inventory = { resetAt: number; balloon: number; fireworks: number; pie: number };
export type ChatMessage = { id: string; avatarId: string; name: string; text: string; createdAt: number };
export type Wish = ChatMessage & { status: 'approved' | 'pending' };
export type Balloon = {
  id: string; ownerId: string; color: string; createdAt: number; expiresAt: number;
  origin: Vec2; impulseAt: number; vx: number; vz: number; vy: number; revision: number;
  originY?: number; releaseAt?: number;
};
export type Effect = {
  id: string; kind: 'fireworks' | 'pie'; avatarId: string; targetId?: string;
  origin: Vec2; target?: Vec2; startedAt: number; impactAt: number; endsAt: number;
  seed: number; hit?: boolean;
};
export type Celebration = {
  id: string; startedAt: number; gatherUntil: number; cakeAt: number; fireworksAt: number;
  endsAt: number; center: Vec2; participants: { id: string; target: Vec2 }[]; seed: number;
};
export type WorldState = {
  seq: number; serverTime: number; avatars: AvatarState[]; chat: ChatMessage[];
  wishes: Wish[]; balloons: Balloon[]; effects: Effect[]; celebration: Celebration | null;
};
export type WorldDelta = {
  avatars?: AvatarState[]; removedAvatarIds?: string[]; chat?: ChatMessage[];
  wishes?: Wish[]; removedChatIds?: string[]; removedWishIds?: string[];
  balloons?: Balloon[]; removedBalloonIds?: string[]; effects?: Effect[];
  removedEffectIds?: string[]; celebration?: Celebration | null;
};
export type ClientMessage =
  | { type: 'join'; requestId: string; name: string; appearance: AvatarAppearance }
  | { type: 'avatar'; requestId: string; name: string; appearance: AvatarAppearance }
  | { type: 'chat'; requestId: string; text: string }
  | { type: 'wish'; requestId: string; text: string }
  | { type: 'rsvp'; requestId: string; status: RsvpStatus }
  | { type: 'move'; requestId: string; target: Vec2 }
  | { type: 'ability'; requestId: string; ability: 'balloon' | 'fireworks' | 'pie'; targetId?: string }
  | { type: 'visibility'; visible: boolean }
  | { type: 'resync'; lastSeq: number }
  | { type: 'leave'; requestId: string };
export type ServerMessage =
  | ({ type: 'snapshot'; selfId: string; inventory: Inventory; appearance: AvatarAppearance | null; name: string } & WorldState)
  | ({ type: 'delta'; seq: number; serverTime: number } & WorldDelta)
  | { type: 'inventory'; inventory: Inventory }
  | { type: 'ack'; requestId: string; note?: string }
  | { type: 'error'; requestId?: string; code: string; message: string; retryAfterMs?: number };
export type ModerationAction = { action: 'delete-chat' | 'delete-wish' | 'approve-wish' | 'mute' | 'unmute'; id: string };
export type Bootstrap = { config: import('./config.ts').PublicPartyConfig; selfId: string; name: string; appearance: AvatarAppearance | null; inventory: Inventory; serverTime: number };
export type RendererStats = { fps: number; frameMs: number; drawCalls: number; triangles: number; avatars: number; renderScale: number; simulationMs: number; cpuFrameMs?: number; frameP95Ms?: number; cpuP95Ms?: number; activeParticles?: number; activeBalloons?: number; shadowMaps?: boolean; audioState?: string; memoryMb?: number };
export function applyDelta(state: WorldState, message: Extract<ServerMessage, {type:'delta'}>): WorldState {
  const merge = <T extends {id:string}>(old: T[], additions?: T[], removed?: string[]): T[] => {
    if (!additions?.length && !removed?.length) return old;
    const deleted = new Set(removed ?? []); const map = new Map(old.filter(x => !deleted.has(x.id)).map(x=>[x.id,x]));
    for (const item of additions ?? []) map.set(item.id,item);
    return [...map.values()];
  };
  const chat = merge(state.chat,message.chat,message.removedChatIds);
  return { seq:message.seq,serverTime:message.serverTime,
    avatars:merge(state.avatars,message.avatars,message.removedAvatarIds),
    chat:chat.length > 100 ? chat.slice(-100) : chat,
    wishes:merge(state.wishes,message.wishes,message.removedWishIds),
    balloons:merge(state.balloons,message.balloons,message.removedBalloonIds),
    effects:merge(state.effects,message.effects,message.removedEffectIds),
    celebration:message.celebration === undefined ? state.celebration : message.celebration };
}
